import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  IconButton,
  TextField,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Chip
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import { SERVER_BASE_URL } from "../Config";

interface Address {
  id: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  isDefault: boolean;
}

interface PatientAddressManagerProps {
  patientId: number | null;
  setPatientId: (id: number) => void;
  createPatientFn: () => Promise<number | false>; // Function passed from parent to create patient and return ID
  onAddressesChange?: (patientId: number) => void; // Callback when addresses are added/updated/deleted
}

const PatientAddressManager: React.FC<PatientAddressManagerProps> = ({ patientId, setPatientId, createPatientFn, onAddressesChange }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    street: "",
    city: "",
    state: "",
    zip: "",
    isDefault: false
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (patientId !== null) {
      // Fetch addresses for the patient; assume this is handled by parent component or via callback
      fetchAddressesForPatient(patientId);
    }
  }, [patientId]);

  const fetchAddressesForPatient = async (patientId: number) => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/patient_address/${patientId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("providerToken")}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch addresses");
      }
      const data = await response.json();
      setAddresses(data);
    } catch (err) {
      // @ts-ignore
      setError(`Error fetching addresses: ${err.message}`);
    }
  };


  const handleNewAddressClick = async (e?: React.MouseEvent) => {
    // Prevent event from bubbling up to parent modal
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (patientId === null) {
      try {
        // Call the create patient function provided by the parent
        const newPatientId = await createPatientFn();
        if (newPatientId === false) {
          setError("Failed to create patient");
          return;
        }
        setPatientId(newPatientId); // Update state with the new patient ID

        // Wait a bit for state to settle after patient creation
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        // @ts-ignore
        setError(`Error creating patient: ${error.message}`);
        return;
      }
    }

    // Set form data for new address
    setFormData({
      id: null,
      street: "",
      city: "",
      state: "",
      zip: "",
      isDefault: addresses.length === 0 // Default to true if it's the first address
    });
    setIsEditing(false);
    setShowAddressModal(true);
  };

  const handleEditAddressClick = (address: Address) => {
    // @ts-ignore
    setFormData(address);
    setIsEditing(true);
    setShowAddressModal(true);
  };

  const handleCloseModal = () => {
    setShowAddressModal(false);
  };

  const handleSaveAddress = async () => {
    try {
      if (isEditing && formData.id !== null) {
        // Update existing address
        const response = await fetch(`${SERVER_BASE_URL}/patient_address/${formData.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("providerToken")}`
          },
          body: JSON.stringify({
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            makeDefault: formData.isDefault
          })
        });
        if (!response.ok) {
          throw new Error("Failed to update address");
        }
        // If this address is now default, update all others to not be default
        if (formData.isDefault) {
          setAddresses((prevAddresses) =>
            prevAddresses.map((addr) => ({
              ...addr,
              isDefault: addr.id === formData.id
            }))
          );
        } else {
          // @ts-ignore
          setAddresses((prevAddresses) =>
            prevAddresses.map((addr) =>
              addr.id === formData.id ? { ...formData } : addr
            )
          );
        }
      } else if (patientId !== null) {
        // Add new address
        const response = await fetch(`${SERVER_BASE_URL}/patient_address/${patientId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("providerToken")}`
          },
          body: JSON.stringify({
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            makeDefault: formData.isDefault
          })
        });
        if (!response.ok) {
          throw new Error("Failed to save address");
        }
        const data = await response.json();
        const newAddress = { ...formData, id: data.address_id, isDefault: data.isDefault };
        // If new address is default, update others
        if (data.isDefault) {
          setAddresses((prevAddresses) => [
            ...prevAddresses.map(addr => ({ ...addr, isDefault: false })),
            newAddress
          ]);
        } else {
          setAddresses((prevAddresses) => [...prevAddresses, newAddress]);
        }
      }
      handleCloseModal();
      // Notify parent that addresses have changed
      if (onAddressesChange && patientId) {
        onAddressesChange(patientId);
      }
    } catch (error) {
      // @ts-ignore
      setError(`Error saving address: ${error.message}`);
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }
    try {
      const response = await fetch(`${SERVER_BASE_URL}/patient_address/${addressId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("providerToken")}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to delete address");
      }
      // Remove the address from state and refresh to get updated default
      if (patientId) {
        fetchAddressesForPatient(patientId);
      }
      // Notify parent that addresses have changed
      if (onAddressesChange && patientId) {
        onAddressesChange(patientId);
      }
    } catch (error) {
      // @ts-ignore
      setError(`Error deleting address: ${error.message}`);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
      )}
      <Typography variant="h6" gutterBottom>
        Manage Addresses (Optional)
      </Typography>
      {/*<RadioGroup value={selectedAddressId?.toString() || ""} onChange={handleAddressSelect}>*/}
      {/*  {addresses.map((address, index) => (*/}
      {/*    <Card key={address.id} sx={{ mb: 2 }}>*/}
      {/*      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>*/}
      {/*        <Box>*/}
      {/*          <FormControlLabel*/}
      {/*            value={(index + 1).toString()} // Use index + 1 to match the array position correctly*/}
      {/*            control={<Radio />}*/}
      {/*            label={`${address.street}, ${address.city}, ${address.state} ${address.zip} ${index === 0 ? "(Default)" : ""}`}*/}
      {/*          />*/}
      {/*        </Box>*/}
      {/*        <IconButton onClick={() => handleEditAddressClick(address)} color="primary">*/}
      {/*          <EditIcon />*/}
      {/*        </IconButton>*/}
      {/*      </CardContent>*/}
      {/*    </Card>*/}
      {/*  ))}*/}
      {/*</RadioGroup>*/}
      {addresses.map((address) => (
        <Card key={address.id} sx={{ mb: 2 }}>
          <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
              <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                {`${address.street}, ${address.city}, ${address.state} ${address.zip}`}
              </Typography>
              {address.isDefault && (
                <Chip label="Default" size="small" color="primary" />
              )}
            </Box>
            <Box sx={{ flexShrink: 0, display: "flex" }}>
              <IconButton onClick={() => handleEditAddressClick(address)} color="primary">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteAddress(address.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}
      <Button variant="contained" color="primary" onClick={(e) => handleNewAddressClick(e)} type="button" sx={{ mt: 1 }}>
        + New Address
      </Button>

      <Modal
        open={showAddressModal}
        onClose={handleCloseModal}
        aria-labelledby="address-modal"
        aria-describedby="modal-to-create-or-edit-address"
        sx={{ zIndex: 1500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 400 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 4 }
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {isEditing ? "Edit Address" : "Create New Address"}
          </Typography>
          <TextField
            label="Street"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.street}
            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
          />
          <TextField
            label="City"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
          <TextField
            label="State"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          />
          <TextField
            label="ZIP"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={formData.zip}
            onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              />
            }
            label="Make this the default address"
            sx={{ mb: 2 }}
          />
          <Button variant="contained" color="primary" onClick={handleSaveAddress}>
            {isEditing ? "Update Address" : "Save Address"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default PatientAddressManager;
